<script setup lang="ts">
import useUserStore from '~/store/user'
import usePoolStore from '~/store/pool'
import UserNotConnected from '../Common/UserNotConnected.vue'
import NavigationTabs from './NavigationTabs.vue'
import SectionInfos from './SectionInfos.vue'
import PicturePopUp from './PicturePopUp.vue'
import SectionStation from './SectionStation.vue'
import SectionAccess from './SectionAccess.vue'
import SectionPictures from './SectionPictures.vue'
import SectionNear from './SectionNear.vue'
import FeedbackList from '../FeedbackList/FeedbackList.vue'
import AlertMessage from './AlertMessage.vue'
import TeslaAlertMessage from './TeslaAlertMessage.vue'
import TeslaPopUp from './TeslaPopUp.vue'
import AlertPopUp from './AlertPopUp.vue'

defineProps({
    isPartner: {
        type: Boolean,
        default: false
    },
    isTablet: {
        type: Boolean,
        default: false
    },
    isMap: {
        type: Boolean,
        default: false
    },
    isLoading: {
        type: Boolean,
        default: true
    }
})

defineEmits(['showPricesInDrawer'])

const poolStore = usePoolStore()
const userStore = useUserStore()
const { locale } = useI18n()

const currentSection = ref('infos')
const isRatingInfosPopUpOpen = ref(false)
const isPictureModalOpen = ref(false)
const pictures = ref([])
const pictureIndex = ref(0)
const isTeslaPopupOpen = ref(false)
const isAlertPopupOpen = ref(false)

const openDirectionLink = () => {
    if (poolStore.getDirectionLink) {
        const newWnd = window.open(poolStore.getDirectionLink, '_blank')

        if (newWnd) {
            newWnd.opener = null
        }
    }
}

const onClickOnEditPool = () => {
    if (poolStore.poolId) {
        if (window.location !== window?.parent?.location) {
            ;(window.top as Window).postMessage(
                { action: 'openUrl', url: `wizard/${poolStore.poolId}` },
                document.referrer
            )
        } else {
            const newWnd = window.open(
                getChargemapUrl(`/wizard/${poolStore.poolId}`, locale.value),
                '_top'
            )

            if (newWnd) {
                newWnd.opener = null
            }
        }
    }
}

const onClickOnPoolName = () => {
    if (userStore.userId) {
        if (window.location !== window?.parent?.location) {
            ;(window.top as Window).postMessage(
                {
                    action: 'openUrl',
                    url: `${poolStore.slug}.html`,
                    target: '_blank'
                },
                document.referrer
            )
        } else {
            const newWnd = window.open(
                getChargemapUrl(`/${poolStore.slug}.html`, locale.value),
                '_top'
            )

            if (newWnd) {
                newWnd.opener = null
            }
        }
    }
}

const updateSection = (event: any) => {
    currentSection.value = event

    if (event === 'reviews') {
        poolStore.fetchFeedbacksFromCommunityFeedbacks()
    }
}
</script>

<template>
    <Loader v-if="isLoading" class="loader" />
    <div
        v-else
        class="font-inter font-medium pb-3 h-full w-full bottom-0 border-1 border-gray-l-300 absolute bg-white flex flex-col"
        :class="[isTablet ? (isMap ? '' : 'rounded-l-20') : 'rounded-t-10']"
    >
        <AlertMessage
            v-if="poolStore.getWarningMessage"
            :text="poolStore.getWarningMessage"
            :class="{
                'rounded-tl-20': isTablet && !isMap,
                'rounded-t-10': !isTablet
            }"
            @showAlertPopup="isAlertPopupOpen = true"
        />
        <TeslaAlertMessage
            v-else-if="
                poolStore.isTeslaCompatible &&
                poolStore.canChargeWithChargemapPass &&
                !isPartner
            "
            :class="[isTablet ? 'rounded-tl-20' : 'rounded-t-10']"
            @showTeslaPopup="isTeslaPopupOpen = true"
        />
        <div
            class="flex justify-between items-center mx-1 px-3 bg-white"
            :class="[isTablet ? 'rounded-tl-20' : 'rounded-t-10']"
        >
            <span
                class="py-4 pr-10 text-0.5xl"
                :class="[
                    userStore.userId && 'cursor-pointer',
                    poolStore.isDeleted
                        ? 'text-red-600 line-through'
                        : 'text-secondary-info'
                ]"
                @click="onClickOnPoolName"
            >
                {{ poolStore.poolName }}
            </span>
        </div>

        <NavigationTabs
            class="px-3"
            :current-section
            @updateSection="updateSection"
        />

        <div
            v-if="currentSection === 'infos'"
            class="overflow-y-auto h-full bg-white"
            :class="[isTablet && 'rounded-bl-20']"
        >
            <Loader v-if="poolStore.poolDetailLoader" />
            <SectionInfos
                v-else-if="poolStore.poolDetail"
                :is-partner
                @updateSection="($event: any) => (currentSection = $event)"
                @showReviewPopUp="isRatingInfosPopUpOpen = true"
                @showPricesInDrawer="$emit('showPricesInDrawer')"
            />
        </div>

        <div
            v-else-if="currentSection === 'stations'"
            class="overflow-y-auto bg-white p-4 h-full"
            :class="[isTablet && 'rounded-bl-20']"
        >
            <div v-if="!userStore.userId" class="flex items-center px-4">
                <UserNotConnected />
            </div>
            <SectionStation v-else-if="poolStore.poolId" />
        </div>

        <div
            v-else-if="currentSection === 'access'"
            class="overflow-y-auto bg-white p-4 h-full"
            :class="[isTablet && 'rounded-bl-20']"
        >
            <div v-if="!userStore.userId" class="flex items-center px-4">
                <UserNotConnected />
            </div>
            <SectionAccess v-else-if="poolStore.poolId" />
        </div>

        <div
            v-else-if="currentSection === 'pictures'"
            class="overflow-y-auto bg-white p-4 h-full"
            :class="[isTablet && 'rounded-bl-20']"
        >
            <div v-if="!userStore.userId" class="flex items-center px-4">
                <UserNotConnected />
            </div>
            <SectionPictures
                v-else-if="poolStore.poolId"
                :is-exploitation="false"
                @sendPictures="pictures = $event"
                @pictureIndex="pictureIndex = $event"
                @showPicturePopUp="isPictureModalOpen = true"
            />
        </div>

        <div
            v-else-if="currentSection === 'reviews'"
            class="overflow-y-auto bg-white py-4 px-2 425:px-4 h-full"
            :class="[isTablet && 'rounded-bl-20']"
        >
            <div v-if="!userStore.userId" class="flex items-center px-4">
                <UserNotConnected />
            </div>
            <div v-else class="overflow-x-hidden flex justify-center">
                <FeedbackList :locale :is-partner />
            </div>
        </div>

        <div
            v-else-if="currentSection === 'near'"
            class="overflow-y-auto bg-white p-4 h-full"
            :class="[isTablet && 'rounded-bl-20']"
        >
            <div v-if="!userStore.userId" class="flex items-center px-4">
                <UserNotConnected />
            </div>
            <SectionNear v-else />
        </div>

        <div
            v-if="currentSection !== 'reviews' && userStore.userId"
            class="flex gap-3 pt-3 px-3 shadow-upper-1"
        >
            <ChargemapB2CButton
                v-if="!isPartner"
                type="secondary"
                size="large"
                custom="full-width"
                @click="openDirectionLink"
            >
                {{ $t('gps_itinerary') }}
            </ChargemapB2CButton>
            <ChargemapB2CButton
                type="secondary"
                size="large"
                custom="full-width"
                @click="onClickOnEditPool"
            >
                {{ $t('infos_edit') }}
            </ChargemapB2CButton>
        </div>
    </div>

    <PicturePopUp
        v-if="isPictureModalOpen"
        :photos="pictures"
        :photo-index="pictureIndex"
        @closeModal="isPictureModalOpen = false"
    />

    <div
        v-if="isRatingInfosPopUpOpen"
        class="z-10 absolute w-screen h-screen flex justify-center items-center font-inter"
    >
        <span
            class="bg-black opacity-40 h-screen w-screen absolute"
            @click="isRatingInfosPopUpOpen = false"
        />
        <RatingInfosPopup
            class="z-10"
            @closePopUp="isRatingInfosPopUpOpen = false"
        />
    </div>
    <TeslaPopUp v-if="isTeslaPopupOpen" @close="isTeslaPopupOpen = false" />
    <AlertPopUp
        v-if="isAlertPopupOpen"
        :text="poolStore.getWarningMessage"
        @close="isAlertPopupOpen = false"
    />
</template>

<style lang="scss" scoped>
.loader,
.loader > * {
    @apply bg-white size-full;
    position: relative !important;
}
</style>
